import { memo, useEffect, useLayoutEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { getNodeZIndex, isNodeDraggable, isNodeSelectable } from '../../core';
import { useNodeClickSelect, useNodeDrag } from '../../interactions';
import { selectNodeById, selectNodesDraggable, selectNodeTypes, useMindmapStore, useStoreApi } from '../../store';
import { classNames, measureHandles } from '../../utils';
import { NodeIdProvider } from '../NodeIdContext';
import { DefaultNode } from './DefaultNode';
import { useNodeEvents } from './useNodeEvents';

const DEFAULT_NODE_TYPE = 'default';

export type NodeWrapperProps = { id: string; resizeObserver: ResizeObserver | null };

/**
 * Positions one node and renders its `nodeTypes` component. Subscribes to its own node only (D-006).
 * Hidden until measured, and `node.style` wins over the engine's styles — both as in v11. Draggable nodes get
 * `nopan`, so dragging them moves the node; non-draggable ones pan the canvas (v11, e2e-confirmed).
 */
const NodeWrapperComponent = ({ id, resizeObserver }: NodeWrapperProps) => {
  const internal = useMindmapStore(selectNodeById(id));
  const nodeTypes = useMindmapStore(selectNodeTypes);
  const store = useStoreApi();
  const ref = useRef<HTMLDivElement>(null);
  const nodesDraggable = useMindmapStore(selectNodesDraggable);
  const type = internal?.node.type ?? DEFAULT_NODE_TYPE;
  const isDraggable = internal ? isNodeDraggable(internal.node, nodesDraggable) : false;
  const isSelectable = internal ? isNodeSelectable(internal.node) : false;
  useNodeDrag(ref, id, isDraggable);
  useNodeClickSelect(ref, id, isSelectable && !isDraggable);
  const { onClick, onDoubleClick, onMouseEnter, onContextMenu } = useNodeEvents(id);

  useEffect(() => {
    const element = ref.current;
    if (!element || !resizeObserver) return undefined;
    resizeObserver.observe(element);
    return () => resizeObserver.unobserve(element);
  }, [resizeObserver]);

  // A new type renders different handles without necessarily changing the size, so measure them again.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const { viewport, setHandleBounds } = store.getState();
    setHandleBounds([{ id, handleBounds: measureHandles(element, viewport.zoom) }]);
  }, [store, id, type]);

  if (!internal) return null;
  const { node, positionAbsolute, width, height } = internal;
  const NodeComponent = type in nodeTypes ? nodeTypes[type] : DefaultNode;
  const style: CSSProperties = {
    transform: `translate(${positionAbsolute.x}px, ${positionAbsolute.y}px)`,
    visibility: width && height ? 'visible' : 'hidden',
    zIndex: getNodeZIndex(node),
    ...(node.style as CSSProperties | undefined),
  };

  return (
    <div
      ref={ref}
      data-id={id}
      className={classNames(
        'cm-node',
        `cm-node--${type}`,
        node.className,
        isDraggable && 'nopan',
        isSelectable && 'selectable',
        node.selected && 'selected',
        node.dragging && 'dragging',
      )}
      style={style}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={onMouseEnter}
      onContextMenu={onContextMenu}
    >
      <NodeIdProvider value={id}>
        <NodeComponent
          id={id}
          data={node.data}
          type={type}
          selected={Boolean(node.selected)}
          dragging={Boolean(node.dragging)}
          isConnectable={node.connectable ?? true}
          xPos={positionAbsolute.x}
          yPos={positionAbsolute.y}
          zIndex={getNodeZIndex(node)}
        />
      </NodeIdProvider>
    </div>
  );
};

export const NodeWrapper = memo(NodeWrapperComponent);
